import { useCallback, useState } from 'react';

/**
 * Persistent per-user preference hook. Backed by localStorage under
 * `confluent.workforce.<key>`. Keep this signature stable — when the app
 * gains a server-side user-prefs store, swap the internals here and every
 * caller continues to work.
 */
export function usePref<T>(key: string, defaultValue: T): [T, (next: T) => void] {
  const storageKey = `confluent.workforce.${key}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw === null ? defaultValue : (JSON.parse(raw) as T);
    } catch {
      return defaultValue;
    }
  });
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // localStorage may be unavailable (private mode, quota); no-op
      }
    },
    [storageKey],
  );
  return [value, set];
}
